import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import accessRequestCommentDataService from "services/accessRequestComment";

// --- Initial State ---
const initialState = {
  comments: [],
  transformedComments: {},
  getAccessRequestCommentsStatus: {
    status: "idle",
    error: null,
  },
};

// --- Async Thunks ---
export const getAccessRequestComments = createAsyncThunk(
  "accessRequestComments/getComments",
  async ({ companyId, organizationId, accessRequest }, thunkAPI) => {
    try {
      const res = await accessRequestCommentDataService.getAll(
        companyId,
        organizationId,
        accessRequest
      );
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const addAccessRequestComment = createAsyncThunk(
  "accessRequestComments/addComment",
  async ({ data }, thunkAPI) => {
    try {
      const res = await accessRequestCommentDataService.create(data);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const editAccessRequestComment = createAsyncThunk(
  "accessRequestComments/update",
  async ({ commentId, data }, thunkAPI) => {
    try {
      const res = await accessRequestCommentDataService.update(commentId, data);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

export const deleteAccessRequestComment = createAsyncThunk(
  "accessRequestComments/delete",
  async ({ commentId }, thunkAPI) => {
    try {
      await accessRequestCommentDataService.remove(commentId);
      return { commentId };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message || "Unknown error"
      );
    }
  }
);

// --- Slice ---
const accessRequestCommentsSlice = createSlice({
  name: "Comments",
  initialState,
  reducers: {
    updateCommentState: (state, { payload }) => {
      const { comments } = state;
      const index = comments.findIndex(
        (comment) => payload._id === comment._id
      );
      if (index > -1) {
        comments.splice(index, 1, payload);
      }
    },
    resetAccessRequestCommentsSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccessRequestComments.pending, (state) => {
        state.getAccessRequestCommentsStatus.status = "loading";
        state.getAccessRequestCommentsStatus.error = null;
      })
      .addCase(getAccessRequestComments.fulfilled, (state, action) => {
        state.getAccessRequestCommentsStatus.status = "idle";
        state.comments = action.payload.items;
      })
      .addCase(getAccessRequestComments.rejected, (state, action) => {
        state.getAccessRequestCommentsStatus.status = "failed";
        state.getAccessRequestCommentsStatus.error =
          action.payload || "Failed to fetch comments";
      })
      .addCase(addAccessRequestComment.fulfilled, (state, action) => {
        state.comments.push(action.payload.accessRequestComment);
      })
      .addCase(editAccessRequestComment.fulfilled, (state, { payload }) => {
        const { comments } = state;
        const index = comments.findIndex(
          (comment) => payload._id === comment._id
        );
        if (index > -1) {
          comments.splice(index, 1, payload);
        }
      })
      .addCase(deleteAccessRequestComment.fulfilled, (state, action) => {
        const index = state.comments.findIndex(
          (comment) => action.payload.commentId === comment._id
        );
        if (index > -1) state.comments.splice(index, 1);
      });
  },
});

export const { updateCommentState, resetAccessRequestCommentsSlice } =
  accessRequestCommentsSlice.actions;

export default accessRequestCommentsSlice.reducer;
